import { addCaseDiagnosis, updateCondition } from "@repo/data-store";
import type { CaseId, ConditionId } from "@repo/domain";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { type CodeValue, hasCode, toCodeableConcept } from "@/shared/forms/code-value";

// === Schema & Types ===

export const conditionFormSchema = z.object({
  patientId: z.string().min(1, "Select a patient"),
  code: z.custom<CodeValue | undefined>().refine(hasCode, { message: "Select a condition code" }),
  onsetDate: z.date().nullable(),
  note: z.string(),
});

export type ConditionFormValues = z.infer<typeof conditionFormSchema>;

export const DEFAULT_CONDITION_FORM_VALUES: ConditionFormValues = {
  patientId: "",
  code: undefined,
  onsetDate: null,
  note: "",
};

// === Submit ===

/** Adds a problem-list Condition to the patient's open case from validated form values. */
export function addCaseDiagnosisFromForm(value: ConditionFormValues, caseId: CaseId, id: string): void {
  if (!value.code) return;
  addCaseDiagnosis({
    id,
    caseId,
    code: toCodeableConcept(value.code),
    clinicalStatus: "active",
    verificationStatus: "provisional",
    ...(value.onsetDate && { onsetDateTime: value.onsetDate.toISOString() }),
    ...(value.note.trim() ? { note: value.note.trim() } : {}),
  });
}

/** Corrects an existing Condition's onset/note from validated form values. Code is immutable. */
export function updateConditionFromForm(value: ConditionFormValues, conditionId: ConditionId): void {
  updateCondition({
    conditionId,
    ...(value.onsetDate ? { onsetDateTime: value.onsetDate.toISOString() } : {}),
    note: value.note.trim(),
  });
}

export { useAppForm };
